import { CriticalInfoDocument, CriticalSystem, CriticalSystemType, CriticalSystemTypes } from '@/types/CriticalInfo';
import { kebabCase } from 'lodash';

/**
 * Interface for critical system information with all fields combined
 */
export interface CriticalSystemInfo {
	key: string;
	name: string;
	tooltip: string;
	type?: CriticalSystemType;
	showByDefault?: boolean;
	canRemoveBaseInstance?: boolean;
}

export const CriticalSystemField = {
	Location: 'location',
	ToolNeeded: 'tool_needed',
	HowToOperate: 'how_to_operate',
	Notes: 'notes',
} as const;

export type CriticalSystemField = (typeof CriticalSystemField)[keyof typeof CriticalSystemField];

/**
 * Default important systems that must be included in every house.
 * These systems are essential for emergency situations and cannot be removed by users.
 */
export const DEFAULT_CRITICAL_SYSTEMS: CriticalSystemInfo[] = [
	{
		key: 'switchboard',
		name: 'Switchboard',
		tooltip:
			'Main electrical panel with fuses/circuit breakers that controls power to your entire property,' +
			' often in a cupboard or garage',
	},
	{
		key: 'mains-water-shutoff',
		name: 'Mains Water Shutoff',
		tooltip:
			'Main valve to shut off water supply to the entire property - usually at the street boundary' +
			' with the water meter or wherever water enters your property ',
		canRemoveBaseInstance: true,
	},
];

/**
 * Additional systems that users can optionally add to their house information.
 * These provide a standardized set of common house systems for consistency.
 */
export const ADDITIONAL_SYSTEMS: CriticalSystemInfo[] = [
	{
		key: 'gas-shutoff',
		name: 'Gas Shutoff',
		tooltip:
			'Main valve to shut off gas supply to entire property - usually near the gas' +
			' meter outside on the side of the house',
		showByDefault: true,
	},
	{
		key: 'hot-water',
		name: 'Hot Water',
		tooltip: 'Valve to shut off hot water cylinder - may be gas or electric, locate the isolation valve',
		showByDefault: true,
	},
	{
		key: 'water-meter',
		name: 'Water Meter',
		tooltip:
			'Where the water company measures water usage - usually a blue box or pit in the ground at street boundary',
		showByDefault: true,
	},
	{
		key: 'smoke-alarm-panel',
		name: 'Smoke Alarm',
		tooltip:
			'Main control panel for hardwired smoke alarm system if you have interconnected' +
			' alarms - individual battery alarms otherwise.',
		showByDefault: true,
	},
	// {
	// 	key: "spare-keys",
	// 	name: "Spare Keys",
	// 	tooltip: "Location/holder of all house keys for emergency access - including lockboxes, " +
	// 		"hidden keys, trusted neighbours, security company etc.",
	// 	showByDefault: true,
	// },
	{
		key: 'internet-entry',
		name: 'Internet Entry',
		tooltip:
			'Where internet enters your property - ONT box for fiber, cable box, or other entry point. ' +
			'Typically where the router is plugged into.',
		showByDefault: true,
	},
	{
		key: 'wastewater',
		name: 'Wastewater',
		tooltip:
			'Septic tank or greywater system location and the alarm for it location - ' +
			'for properties that have these systems, or main sewer access point if known',
		showByDefault: true,
	},

	// Gas meter, electrical meter, security alarm, wastewater, waterpump

	{
		key: 'gas-meter',
		name: 'Gas Meter',
		tooltip:
			'Where the gas company measures gas usage - usually outside on the side of the house near' +
			' the street for meter readings and utility access. Emergency gas contacts are usually on your bill',
		showByDefault: true,
	},
	{
		key: 'electrical-meter',
		name: 'Electrical Meter',
		tooltip:
			'Where power companies measure electricity usage - usually outside ' +
			'on the side of house nearer the street for meter readings and utility access',
		showByDefault: true,
	},
	// {
	// 	key: "security-alarm-panel",
	// 	name: "Security Alarm",
	// 	tooltip: "The main control panel for your security system, usually hidden in a cupboard, garage or roof " +
	// 		"space. A codepad is usually near the main entrance. Sensors and sirens will be spread around the property."
	// },
	// {
	// 	key: "pool-equipment",
	// 	name: "Pool Equipment",
	// 	tooltip: "Pool pump, filter, chlorinator controls - usually in equipment shed or designated area"
	// },
	// {
	// 	key: "solar-inverter",
	// 	name: "Solar Inverter",
	// 	tooltip: "Converts solar panel DC power to AC - usually mounted on wall near electrical meter"
	// },
	{
		key: 'water-pump',
		name: 'Water Pump',
		tooltip: 'Bore pump, pressure pump, or tank water pump - provides water pressure to property',
		showByDefault: true,
	},
	// {
	// 	key: "air-con",
	// 	name: "Air Con",
	// 	tooltip: "Air conditioning or heat pump controls - outdoor unit location and indoor controller"
	// }
];

export const REMOVABLE_DEFAULT_SYSTEMS: CriticalSystemInfo[] = DEFAULT_CRITICAL_SYSTEMS.filter(
	(system) => system.canRemoveBaseInstance,
).map((system) => ({
	...system,
	type: CriticalSystemTypes.Critical,
	showByDefault: true,
}));

export const ADDABLE_SYSTEMS: CriticalSystemInfo[] = [...REMOVABLE_DEFAULT_SYSTEMS, ...ADDITIONAL_SYSTEMS];

/**
 * Helper function to get system info by name
 */
export const getSystemInfoByName = (name: string): CriticalSystemInfo => {
	return (
		DEFAULT_CRITICAL_SYSTEMS.find((s) => s.name === name) ||
		ADDITIONAL_SYSTEMS.find((s) => s.name === name) || { key: kebabCase(name), name, tooltip: '' }
	);
};

/**
 * Helper function to get system info by key
 */
export const getSystemInfoByKey = (key: string): CriticalSystemInfo | undefined => {
	return DEFAULT_CRITICAL_SYSTEMS.find((s) => s.key === key) || ADDITIONAL_SYSTEMS.find((s) => s.key === key);
};

/**
 * Converts the form state to a JSON document format for storage.
 * This transformation prepares the data for API submission with versioning.
 */
export const formToDocument = (systems: CriticalSystem[]): CriticalInfoDocument => {
	return {
		systems,
		version: '1.0',
	};
};

/**
 * Converts a JSON document back to form state for editing.
 * This enables bidirectional data flow for the important information.
 */
export const documentToForm = (document: CriticalInfoDocument): CriticalSystem[] => {
	return document.systems;
};

/**
 * Creates an empty system with default fields based on the system name.
 * Special cases are identified by their key rather than name.
 */
export const createEmptySystem = (
	name: string,
	type: CriticalSystemType = CriticalSystemTypes.Custom,
): CriticalSystem => {
	const defaultFields = [
		{ name: CriticalSystemField.Location, value: '' },
		{ name: CriticalSystemField.Notes, value: '' },
	];

	// Add additional fields based on system type. This might be needed in the future
	// const systemInfo = getSystemInfoByName(name);
	//
	// if (systemInfo.key === "mains-water-shutoff") {
	// 	defaultFields.push({ name: "tool_needed", value: "" });
	// 	defaultFields.push({ name: "how_to_operate", value: "" });
	// }

	return {
		name,
		fields: defaultFields,
		type,
	};
};

/**
 * Strips a trailing instance number from a system name, e.g. "Geyser 2" -> "Geyser".
 */
export const getSystemCategoryName = (systemName: string) => systemName.replace(/\s+\d+$/, '');

export const getSystemInstanceLabel = (systemName: string) => {
	const categoryName = getSystemCategoryName(systemName);
	const suffix = systemName.slice(categoryName.length).trim();
	return suffix || '1';
};

export const getSystemLocation = (system: CriticalSystem) =>
	system.fields.find((field) => field.name === CriticalSystemField.Location)?.value.trim();

export const hasSystemNotes = (system: CriticalSystem) =>
	Boolean(system.fields.find((field) => field.name === CriticalSystemField.Notes)?.value.trim());

export const getSystemNotesSummary = (system: CriticalSystem) => (hasSystemNotes(system) ? 'Notes added' : 'No notes');

export const getSystemRowLabel = (system: CriticalSystem) =>
	`${getSystemInstanceLabel(system.name)}. ${getSystemLocation(system) || 'Location not set'}`;

/**
 * The base instance of a category carries the bare category name (e.g. "Switchboard");
 * additional instances carry a numeric suffix ("Switchboard 2").
 */
export const isBaseInstance = (systemName: string) => getSystemCategoryName(systemName) === systemName;

/**
 * Removability is independent of the system's category type. Only the original
 * required instance of a critical system is locked — every additional instance,
 * and every suggested/custom system, can be removed.
 */
export const canRemoveSystem = (system: CriticalSystem) =>
	system.type !== CriticalSystemTypes.Critical ||
	!isBaseInstance(system.name) ||
	getSystemInfoByName(system.name).canRemoveBaseInstance === true;

export const getNextSystemInstanceName = (systems: CriticalSystem[], systemName: string) => {
	const categoryName = getSystemCategoryName(systemName);
	const existingNames = new Set(systems.map((system) => system.name));

	if (!existingNames.has(categoryName)) return categoryName;

	let instanceNumber = 2;
	let nextName = `${categoryName} ${instanceNumber}`;

	while (existingNames.has(nextName)) {
		instanceNumber += 1;
		nextName = `${categoryName} ${instanceNumber}`;
	}

	return nextName;
};

export const getNextSystemInstance = (systems: CriticalSystem[], systemName: string) => {
	const sourceSystem = systems.find((system) => system.name === systemName);
	const nextName = getNextSystemInstanceName(systems, systemName);
	const nextType =
		sourceSystem?.type === CriticalSystemTypes.Critical ? CriticalSystemTypes.Custom : sourceSystem?.type;

	return { nextName, nextType };
};

export const hasOtherInstanceInCategory = (systems: CriticalSystem[], systemName: string) => {
	const categoryName = getSystemCategoryName(systemName);
	return systems.some((system) => system.name !== systemName && getSystemCategoryName(system.name) === categoryName);
};

/**
 * A system is "documented" once any of its fields has a non-whitespace value.
 */
export const isSystemDocumented = (system: CriticalSystem) => system.fields.some((field) => field.value.trim() !== '');

export const getDocumentedSystemNames = (systems: CriticalSystem[]) =>
	new Set(systems.filter(isSystemDocumented).map((system) => system.name));

/**
 * A category group is "documented" once any of its instances is documented.
 */
export const isGroupDocumented = (systems: CriticalSystem[]) => systems.some(isSystemDocumented);

export const groupSystemsByCategory = (systems: CriticalSystem[]) => {
	const groups: Array<{ categoryName: string; systems: CriticalSystem[] }> = [];
	const indexByCategory = new Map<string, number>();

	for (const system of systems) {
		const categoryName = getSystemCategoryName(system.name);
		const existingIndex = indexByCategory.get(categoryName);

		if (existingIndex === undefined) {
			indexByCategory.set(categoryName, groups.length);
			groups.push({ categoryName, systems: [system] });
		} else {
			groups[existingIndex].systems.push(system);
		}
	}

	return groups;
};
